import React from 'react'
import {Box} from '@mui/material'
import {TimeslotProps} from 'src/types/types'
import ActionIconButton from 'src/components-new/primitives/ActionIconButton'
import Button from 'src/components-new/primitives/Button'
import InlineMessage from 'src/components-new/feedback/InlineMessage'
import SelectNew from 'src/components-new/inputs/SelectNew'
import {TIMESLOT_OPTIONS, WEEK_DAY_OPTIONS} from '../dayTimeOptions'

interface TimeSlotsFieldProps {
  value: TimeslotProps[]
  onChange: (value: TimeslotProps[]) => void
  error?: string
  disabled?: boolean
}

/**
 * Controlled repeatable day/from/to rows — new-style equivalent of
 * src/components/elements/TimeslotsSelector/TimeslotsSelector.tsx (which already used
 * components-new's Button/ActionIconButton for this exact purpose, just with antd Selects
 * for the 3 dropdowns). `showSearch` is added on the time selects (96 quarter-hour options
 * each) since a plain dropdown that long is painful to scroll — a usability improvement,
 * not a data/behavior change.
 */
export default function TimeSlotsField({value, onChange, error, disabled}: TimeSlotsFieldProps): JSX.Element {
  const handleAdd = () => {
    onChange([...value, {day: undefined, from: undefined, to: undefined}])
  }

  const handleUpdate = (index: number, next: TimeslotProps) => {
    const updated = [...value]
    updated[index] = next
    onChange(updated)
  }

  const handleRemove = (index: number) => {
    const updated = [...value]
    updated.splice(index, 1)
    onChange(updated)
  }

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
      <Button variant="secondary" onClick={handleAdd} disabled={disabled} sx={{alignSelf: 'flex-start'}}>
        {'Aggiungi fascia oraria'}
      </Button>

      {error && <InlineMessage severity="error">{error}</InlineMessage>}

      {value.map((slot, index) => (
        <Box key={index} sx={{display: 'flex', gap: 1, alignItems: 'flex-end', flexWrap: 'wrap'}}>
          <SelectNew
            label="Giorno"
            value={slot.day ?? ''}
            onChange={(day) => handleUpdate(index, {...slot, day})}
            options={WEEK_DAY_OPTIONS}
            disabled={disabled}
            sx={{flex: '1 1 160px'}}
          />
          <SelectNew
            label="Dalle"
            value={slot.from ?? ''}
            onChange={(from) => handleUpdate(index, {...slot, from})}
            options={TIMESLOT_OPTIONS}
            showSearch
            disabled={disabled}
            sx={{flex: '1 1 120px'}}
          />
          <SelectNew
            label="A"
            value={slot.to ?? ''}
            onChange={(to) => handleUpdate(index, {...slot, to})}
            options={TIMESLOT_OPTIONS}
            showSearch
            disabled={disabled}
            sx={{flex: '1 1 120px'}}
          />
          <ActionIconButton variant="delete" onClick={() => handleRemove(index)} disabled={disabled} sx={{mb: '4px'}} />
        </Box>
      ))}
    </Box>
  )
}
